import { Col, ControlLabel, FormGroup, HelpBlock } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { TEAM_GROUPS } from '../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../helpers/features/featureFlags';
import useTeamGroups from '../../hooks/api/teams/teamGroups/useTeamGroups';
import useTeams from '../../hooks/api/teams/useTeams';
import Badge from '../generic/Badge/Badge';
import Flex from '../generic/Flex';
import Select from '../generic/Select/Select';
import type { SelectOption } from '../../types';

type Props = {
  // The non-empty "All projects" breadcrumb selection. A team group is a `Teams` row, so it arrives as its own id.
  selectedTeamIds: number[];
  // The release groups of the selected teams. The modal owns these so it submits only a release group shown here.
  releaseGroupOptions: Array<SelectOption<number>>;
  isLoadingReleaseGroups: boolean;
  releaseGroupId: number | null;
  onReleaseGroupChange: (releaseGroupId: number | null) => void;
  isDisabled?: boolean;
};

/**
 * Shows which teams / team groups the Global Package Report Bundle is scoped to, and offers a release-group
 * drill-down across them, mirroring the Global Issue CSV Export card.
 */
export const BulkReportScopeField: VoidFunctionComponent<Props> = ({
  selectedTeamIds,
  releaseGroupOptions,
  isLoadingReleaseGroups,
  releaseGroupId,
  onReleaseGroupChange,
  isDisabled = false,
}) => {
  const { data: teams } = useTeams();
  const isTeamGroupsEnabled = useIsFeatureEnabled(TEAM_GROUPS);
  const { data: teamGroups } = useTeamGroups(isTeamGroupsEnabled);
  const selectedNames = [...(teams ?? []), ...(teamGroups ?? [])]
    .filter((team) => selectedTeamIds.includes(team.id))
    .map((team) => team.name);

  const selectedReleaseGroupOption = releaseGroupOptions.find((option) => option.value === releaseGroupId) ?? null;

  return (
    <FormGroup>
      <Col componentClass={ControlLabel} sm={2}>
        Scope
      </Col>
      <Col sm={10}>
        <Flex justify="flex-start" wrap="wrap" gap={8} className="padding-top-xs margin-bottom-xs">
          {(selectedNames.length > 0 ? selectedNames : ['the selected team']).map((name) => (
            <Badge key={name} color="gray">{name}</Badge>
          ))}
        </Flex>
        <Select<SelectOption<number>>
          id="bulkReportReleaseGroup"
          aria-label="Release group"
          isClearable
          isDisabled={isDisabled}
          isLoading={isLoadingReleaseGroups}
          placeholder="All release groups"
          value={selectedReleaseGroupOption}
          options={releaseGroupOptions}
          onChange={(option) => onReleaseGroupChange(option?.value ?? null)}
        />
        <HelpBlock>Selecting a release group reports on the packages in its latest release.</HelpBlock>
      </Col>
    </FormGroup>
  );
};
